/** Number and date formatting for the UI. Always en-US so the digits are stable across locales and in tests. */
const INT = new Intl.NumberFormat('en-US');

export const fmtInt = (n: number): string => INT.format(Math.round(n));
/** The seed's `fmt` (INV-HLP-08): round half away from the binary tie, then fixed digits. */
export const fmt = (x: number, d = 1): string => (Math.round(x * 10 ** d) / 10 ** d).toFixed(d);
/** `toFixed`, exactly as the engine's headline strings format the same numbers (ENGINE-SPEC §7.6). */
export const fmt1 = (n: number): string => n.toFixed(1);
export const fmt2 = (n: number): string => n.toFixed(2);
export const fmtSigned1 = (n: number): string => `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(1)}`;
export const fmtInterval = (ci: readonly [number, number], digits = 1): string =>
	`${ci[0].toFixed(digits)}–${ci[1].toFixed(digits)}`;

/** p-values: three decimals, or `<0.001`. */
export function fmtP(p: number): string {
	if (!Number.isFinite(p)) return '—';
	return p < 0.001 ? '<0.001' : p.toFixed(3);
}

/** Significant-figure formatting for channel units (1.26, 0.0135, 12.3). */
export function fmtSig(n: number, sig = 3): string {
	if (!Number.isFinite(n)) return '—';
	if (n === 0) return '0';
	const abs = Math.abs(n);
	if (abs >= 100) return n.toFixed(0);
	return Number(n.toPrecision(sig)).toString();
}

export function fmtBytes(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
	if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
	return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** 0:41, 12:05, 1:02:03 */
export function fmtElapsed(ms: number): string {
	const total = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const two = (x: number) => String(x).padStart(2, '0');
	return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

export function fmtDate(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

/** The clamp the ramps and strips share: 0–100. */
export const clamp100 = (n: number): number => Math.max(0, Math.min(100, n));
